import { useState, useEffect } from "react";
import { loadData, storeData, getCached } from "./s3Data";
import { useIsMobile } from "../useIsMobile";

const CATEGORIES = ["Produce", "Dairy", "Meat & Seafood", "Bakery", "Pantry", "Frozen", "Beverages", "Household", "Other"];

const S = {
  root: {
    fontFamily: "'DM Mono', 'Courier New', monospace",
    background: "#0f0f11",
    minHeight: "100vh",
    color: "#e8e6e0",
    paddingTop: 70,
  },
  page: {
    padding: "32px 28px",
    maxWidth: 760,
    margin: "0 auto",
  },
  title: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: 26,
    fontWeight: 600,
    color: "#f0ede6",
    letterSpacing: "-0.02em",
    margin: 0,
    marginBottom: 4,
  },
  sub: {
    fontSize: 10,
    color: "#333",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginBottom: 28,
  },
  addCard: {
    background: "#161618",
    border: "1px solid #2a2a2e",
    borderRadius: 12,
    padding: "18px 20px",
    marginBottom: 20,
  },
  addRow: {
    display: "flex",
    gap: 8,
    alignItems: "flex-end",
    flexWrap: "wrap",
  },
  input: {
    background: "#111113",
    border: "1px solid #2a2a2e",
    borderRadius: 7,
    padding: "8px 11px",
    color: "#e8e6e0",
    fontSize: 13,
    fontFamily: "'DM Mono', monospace",
    outline: "none",
    flex: 1,
    minWidth: 140,
  },
  select: {
    background: "#111113",
    border: "1px solid #2a2a2e",
    borderRadius: 7,
    padding: "8px 11px",
    color: "#e8e6e0",
    fontSize: 12,
    fontFamily: "'DM Mono', monospace",
    outline: "none",
    cursor: "pointer",
    minWidth: 140,
  },
  addBtn: {
    background: "#1D9E75",
    border: "none",
    borderRadius: 7,
    color: "#fff",
    fontSize: 12,
    padding: "8px 16px",
    cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
  },
  catSection: {
    marginBottom: 20,
  },
  catHeader: {
    fontSize: 9,
    color: "#444",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: 8,
    paddingBottom: 6,
    borderBottom: "1px solid #1a1a1d",
  },
  itemRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 12px",
    background: "#161618",
    border: "1px solid #1e1e22",
    borderRadius: 8,
    marginBottom: 6,
  },
  itemCheck: {
    width: 16,
    height: 16,
    cursor: "pointer",
    flexShrink: 0,
    accentColor: "#1D9E75",
  },
  itemText: (done) => ({
    fontSize: 13,
    color: done ? "#333" : "#e8e6e0",
    textDecoration: done ? "line-through" : "none",
    flex: 1,
  }),
  storeBadge: {
    fontSize: 9,
    color: "#555",
    background: "#111113",
    border: "1px solid #2a2a2e",
    borderRadius: 10,
    padding: "2px 8px",
    whiteSpace: "nowrap",
    letterSpacing: "0.04em",
    flexShrink: 0,
  },
  deleteBtn: {
    background: "none",
    border: "none",
    color: "#2a2a2e",
    fontSize: 14,
    cursor: "pointer",
    padding: "0 4px",
    lineHeight: 1,
    fontFamily: "'DM Mono', monospace",
  },
  statusRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  statusText: {
    fontSize: 10,
    color: "#333",
    letterSpacing: "0.06em",
  },
  clearBtn: {
    background: "none",
    border: "1px solid #2a2a2e",
    borderRadius: 6,
    color: "#555",
    fontSize: 10,
    padding: "4px 12px",
    cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
    letterSpacing: "0.04em",
  },
};

export default function ShoppingList() {
  const isMobile = useIsMobile();
  const [items, setItems] = useState(() => getCached("shopping.json", []));
  const [newItem, setNewItem] = useState("");
  const [newStore, setNewStore] = useState("");
  const [newCat, setNewCat] = useState("Other");
  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    loadData("shopping.json", null).then(d => { if (d !== null) setItems(d); }).catch(() => {});
  }, []);

  async function persist(next) {
    setSaveStatus("saving");
    try {
      await storeData("shopping.json", next);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(null), 1500);
    } catch (e) {
      console.error("S3 save failed:", e);
      setSaveStatus("error: " + e.name);
    }
  }

  function addItem() {
    const text = newItem.trim();
    if (!text) return;
    const next = [...items, { id: Date.now(), text, store: newStore.trim(), category: newCat, done: false }];
    setItems(next);
    setNewItem("");
    setNewStore("");
    persist(next);
  }

  function toggle(id) {
    const next = items.map(i => i.id === id ? { ...i, done: !i.done } : i);
    setItems(next);
    persist(next);
  }

  function remove(id) {
    const next = items.filter(i => i.id !== id);
    setItems(next);
    persist(next);
  }

  function clearDone() {
    const next = items.filter(i => !i.done);
    setItems(next);
    persist(next);
  }

  const grouped = CATEGORIES.reduce((acc, cat) => {
    const catItems = items.filter(i => i.category === cat);
    if (catItems.length) acc[cat] = catItems;
    return acc;
  }, {});

  const doneCount = items.filter(i => i.done).length;

  return (
    <div style={S.root}>
      <div style={{ ...S.page, padding: isMobile ? "20px 12px" : S.page.padding }}>
        <p style={S.title}>Shopping List</p>
        <p style={S.sub}>
          {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "Saved" : saveStatus === "error" ? "Save failed" : `${items.length} items · ${doneCount} checked`}
        </p>

        {/* Add item */}
        <div style={S.addCard}>
          <div style={{ ...S.addRow, flexDirection: isMobile ? "column" : "row" }}>
            <input
              style={{ ...S.input, width: isMobile ? "100%" : undefined }}
              value={newItem}
              onChange={e => setNewItem(e.target.value)}
              onKeyDown={e => e.key === "Enter" && addItem()}
              placeholder="Add item…"
            />
            <input
              style={{ ...S.input, width: isMobile ? "100%" : undefined, flex: "0 1 150px", minWidth: 100 }}
              value={newStore}
              onChange={e => setNewStore(e.target.value)}
              onKeyDown={e => e.key === "Enter" && addItem()}
              placeholder="Store (optional)"
            />
            <select
              style={{ ...S.select, width: isMobile ? "100%" : undefined }}
              value={newCat}
              onChange={e => setNewCat(e.target.value)}
            >
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
            <button style={{ ...S.addBtn, width: isMobile ? "100%" : undefined }} onClick={addItem}>
              + Add
            </button>
          </div>
        </div>

        {/* Clear done */}
        {doneCount > 0 && (
          <div style={S.statusRow}>
            <span style={S.statusText}>{doneCount} item{doneCount !== 1 ? "s" : ""} checked</span>
            <button style={S.clearBtn} onClick={clearDone}>Clear checked</button>
          </div>
        )}

        {/* Items by category */}
        {Object.keys(grouped).length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: "#222", fontSize: 13 }}>
            Your list is empty.<br />
            <span style={{ fontSize: 11 }}>Add items above to get started.</span>
          </div>
        ) : (
          Object.entries(grouped).map(([cat, catItems]) => (
            <div key={cat} style={S.catSection}>
              <p style={S.catHeader}>{cat} ({catItems.length})</p>
              {catItems.map(item => (
                <div key={item.id} style={S.itemRow}>
                  <input
                    type="checkbox"
                    style={S.itemCheck}
                    checked={item.done}
                    onChange={() => toggle(item.id)}
                  />
                  <span style={S.itemText(item.done)}>{item.text}</span>
                  {item.store && <span style={S.storeBadge}>{item.store}</span>}
                  <button style={S.deleteBtn} onClick={() => remove(item.id)} title="Remove">×</button>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
